import {
    Box,
    Typography,
} from '@mui/material';
import { DeleteActionButton, EditActionButton, ViewActionButton } from '../../../components/MRTDataGrid/TableActionButtons';
import { MRT_ColumnDef } from 'material-react-table';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser';
import { useNotificationStack } from '../../../app/hooks/useNotificationStack';
import { useConfirmDialogContext } from '../../../components/ConfirmDialog';
import {
    useDeleteCustomerBrandMutation,
    useGetCustomerBrandsQuery
} from '../../../app/redux-fetch/apiProducts';
import { useGetCustomersQuery } from '../../../app/redux-fetch/apiQuery';
import { IDropDownItem } from '../../../app/types';
import AddButton from '../../../components/Buttons/AddButton';
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender';
import FormDropdown from '../../../components/FormDropDown/FormDropdown';
import MRTDataGridV2 from '../../../components/MRTDataGrid/MRTDataGridv2';
import YesNoTypography from '../../../components/YesNoTypograpphy/YesNoTypography';
import SideModalWrapper from '../../Assets/modals/SideModalWrapper';
import { messages } from '../messages';
import { Brand } from '../types';
import TenantBrandForm from './TenantBrandForm';

const TenantBrandTable: React.FC = () => {
    const { formatMessage } = useIntl();
    const { tenantId } = useLoggedInUser();
    const [customerId, setCustomerId] = useState<number>(-1);

    const { data: customers = [], isSuccess: isCustomerLoaded } = useGetCustomersQuery(tenantId);
    const { data = [], isLoading } = useGetCustomerBrandsQuery(
        { customerId },
        { skip: customerId === -1 }
    );

    const [deleteCustomerBrand] = useDeleteCustomerBrandMutation();
    const { enqueueSuccess, enqueueFailure } = useNotificationStack();
    const { confirm } = useConfirmDialogContext();

    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);
    const [selectedBrandId, setSelectedBrandId] = useState(-1);

    const customerOptions: IDropDownItem[] = useMemo(() =>
        customers.map(customer => ({
            id: customer.id,
            name: customer.name
        })), [customers]
    );




    const columns = useMemo<MRT_ColumnDef<Brand>[]>(
        () => [
            {
                accessorKey: 'name',
                Cell: ({ renderedCellValue }: any) => {
                    return <Typography sx={{ fontWeight: 700 }}>{renderedCellValue}</Typography>
                },
                header: formatMessage(messages.name),
            },
            {
                accessorKey: 'systemDefined',
                header: formatMessage(messages.systemDefined),
                muiTableHeadCellProps: {
                    align: 'center',
                },
                muiTableBodyCellProps: {
                    align: 'center',
                },
                Cell: ({ cell }: any) => <YesNoTypography isYes={cell.getValue()} />,
            },
            {
                accessorKey: 'inUse',
                header: formatMessage(messages.inUse),
                muiTableHeadCellProps: {
                    align: 'center',
                },
                muiTableBodyCellProps: {
                    align: 'center',
                },
                Cell: ({ cell }: any) => <YesNoTypography isYes={cell.getValue()} />,
            },
        ],
        [formatMessage]
    );

    if (!isCustomerLoaded) return null;

    return (
        <>
            <SideModalWrapper
                open={createOpen}
                key={'create'}
                headerLabel={formatMessage(messages.createEditMake)}
                onClose={() => {
                    setCreateOpen((pv) => !pv);
                }}
                withBottomBox={false}
            >
                <TenantBrandForm customerId={customerId} />
            </SideModalWrapper>
            <SideModalWrapper
                open={editOpen}
                key={'edit'}
                headerLabel={formatMessage(messages.createEditMake)}
                onClose={() => {
                    setEditOpen((pv) => !pv);
                }}
                withBottomBox={false}
            >
                <TenantBrandForm brandId={selectedBrandId} customerId={customerId} />
            </SideModalWrapper>
            <SideModalWrapper
                open={viewOpen}
                key={'view'}
                headerLabel={formatMessage(messages.viewMake)}
                onClose={() => {
                    setViewOpen((pv) => !pv);
                }}
                withBottomBox={false}
            >
                <TenantBrandForm brandId={selectedBrandId} customerId={customerId} isDisabled={true} />
            </SideModalWrapper>

            <MRTDataGridV2
                initialState={{
                    pagination: { pageSize: 100, pageIndex: 0 },
                    density: 'comfortable',
                    sorting: [{ id: 'name', desc: false }],
                }}
                state={{
                    isLoading,
                }}
                leftHeadingComponent={
                    <FormDropdown
                        id='customerId'
                        items={customerOptions}
                        onChange={async (e: any, name: any, newValue: any) => {
                            setCustomerId(newValue);
                        }}
                        sx={{ width: '400px', mt: '12px' }}
                        value={customerId}
                        label='Customer'
                    />
                }
                rightHeadingComponent={
                    <AddButton
                        text={formatMessage(messages.add)}
                        onClick={() => setCreateOpen(true)}
                        disabled={!customerId || customerId < 1}
                    />
                }
                columns={columns}
                data={data}
                getRowId={(row) => row.id as any}
                enableEditing
                enableRowActions
                positionActionsColumn='last'
                renderRowActions={({ row }: any) => {
                    const brandId = row.original.id;

                    return (
                        <Box sx={{ display: 'flex' }}>
                            <ViewActionButton
                                onClick={() => {
                                    setSelectedBrandId(brandId);
                                    setViewOpen(true);
                                }}
                            />
                            <EditActionButton
                                onClick={() => {
                                    setSelectedBrandId(brandId);
                                    setEditOpen(true);
                                }}
                            />
                            <ConditionalRender condition={!row.original.inUse}>
                                <DeleteActionButton
                                    onClick={async () => {
                                        const confirmed = await confirm({
                                            title: formatMessage(messages.deleteBrandTitle),
                                            message: formatMessage(messages.deleteBrandDescription),
                                            confirmText: formatMessage(messages.deleteBtn),
                                            cancelText: formatMessage(messages.cancel),
                                            severity: 'warning',
                                            confirmButtonColor: 'error',
                                            cancelButtonColor: 'secondary',
                                        })

                                        if (confirmed) {
                                            deleteCustomerBrand({ customerId, brandId: row.original.id })
                                                .unwrap()
                                                .then(() => {
                                                    enqueueSuccess(messages.brandDeleted)
                                                })
                                                .catch(() => {
                                                    enqueueFailure(messages.brandDeleteError)
                                                })
                                        }
                                    }}
                                />
                            </ConditionalRender>
                        </Box>
                    );
                }}
            />
        </>
    );
};

export default TenantBrandTable;
